import { DealRequest, DealRequestDocument } from '@/app/api/types';
import { TFunction } from 'i18next';
import { checkIsDealWithoutRegistry } from './index';

export type Actor = 'factor' | 'buyer' | 'seller';

const SIGN_PRIORITY: Actor[] = ['buyer', 'seller', 'factor'];

const REJECTABLE_ACTORS: Actor[] = ['buyer', 'seller'];

const findSigner = (doc: DealRequestDocument | undefined, actor: Actor) =>
  doc?.signers.find((el) => el.actor === actor);

export const getDocsToSign = (data: DealRequest, actor: Actor) => {
  if (checkIsDealWithoutRegistry(data)) return [data.factoringContract];
  // Реверс всегда подписывают 3 стороны: договор и реестр - все участники.
  if (data.factoringType === 'reverse') {
    return [data.factoringContract, data.debtRegistry];
  }
  if (actor === 'buyer' && !data.isTripartiteContract) {
    return [data.debtRegistry];
  }
  return [data.factoringContract, data.debtRegistry];
};

/**
 * Документы, подпись актора на которых реально ожидается: из требуемых по типу
 * сделки оставляем те, где актор заведён подписантом.
 */
const getDocsAwaitingActorSign = (data: DealRequest, actor: Actor) =>
  getDocsToSign(data, actor).filter((doc) => !!findSigner(doc, actor));

/** Ожидаемые от актора документы, которые он ещё не подписал. */
export const getDocsPendingActorSign = (data: DealRequest, actor: Actor) =>
  getDocsAwaitingActorSign(data, actor).filter(
    (doc) => !findSigner(doc, actor)?.signedAt
  );

/** Актор ещё не подписал хотя бы один из ожидаемых от него документов. */
export const checkIsActorSignPending = (data: DealRequest, actor: Actor) =>
  getDocsPendingActorSign(data, actor).length > 0;

export const getDealSignatureStatusLabel = (
  data: DealRequest,
  viewer: 'seller' | 'buyer' | 'factor',
  t: TFunction<'translation', undefined>
) => {
  if (
    !(data.status === 'contractCreated' || data.status === 'contractRejected')
  ) {
    return undefined;
  }

  const docsAwaiting = (actor: Actor) => getDocsAwaitingActorSign(data, actor);

  const requiredActors = SIGN_PRIORITY.filter(
    (actor) => docsAwaiting(actor).length > 0
  );

  const isActorRejected = (actor: Actor) =>
    docsAwaiting(actor).some(
      (doc) => findSigner(doc, actor)?.action === 'rejected'
    );

  const rejectedActor = requiredActors
    .filter((actor) => REJECTABLE_ACTORS.includes(actor))
    .find(isActorRejected);

  if (rejectedActor) {
    return t(
      rejectedActor === viewer
        ? 'rejected_by_you'
        : `rejected_by_${rejectedActor}`
    );
  }

  const pendingActor = requiredActors.find((actor) =>
    checkIsActorSignPending(data, actor)
  );

  if (!pendingActor) {
    return undefined;
  }

  return t(
    pendingActor === viewer
      ? 'waiting_for_your_sign'
      : `waiting_for_${pendingActor}_sign`
  );
};
